import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import type { Player } from '../types/player';
import { PageLoader } from '../components/LoadingSpinner';
import { TradeSearch } from '../features/trade/components/TradeSearch';
import { RosterPicker } from '../features/trade/components/RosterPicker';
import { searchPlayers } from '../features/trade/utils/search';
import { displayDstName } from '../utils/teams';
import { LeagueFormatSelector, type LeagueFormatType } from '../components/LeagueFormatSelector';
import { useSharedPlayerData } from '../hooks/useSharedPlayerData';
import { createSharedLeagueConfig } from '../utils/sharedLeagueConfig';
import { useMultiRoster } from '../context/MultiRosterContext';
import { explainTrade, type ScoringFormat } from '../utils/leagueInsights';
import {
  evenThis,
  playerTradeValue,
  rankForTrade,
  resolveOnBoard,
  suggestPackages,
  suggestReplacements,
  tradeVerdict,
} from '../utils/tradeDesk';
import { PageShell } from '../components/ui/PageShell';
import { PageHeader } from '../components/ui/PageHeader';
import { Surface } from '../components/ui/Surface';
import { PlayerPhoto } from '../components/PlayerPhoto';

const TradeCalculator = () => {
  const [leagueFormat, setLeagueFormat] = useState<LeagueFormatType>('standard');
  const [scoringFormat, setScoringFormat] = useState<ScoringFormat>('ppr');
  const [youSend, setYouSend] = useState<Player[]>([]);
  const [youGet, setYouGet] = useState<Player[]>([]);
  const [sendSource, setSendSource] = useState<'roster' | 'all'>('roster');

  const { activeRosterPlayers } = useMultiRoster();
  const hasRoster = activeRosterPlayers.length > 0;
  const rosterMode = hasRoster && sendSource === 'roster';

  const leagueConfig = useMemo(() => {
    return createSharedLeagueConfig({
      format: leagueFormat,
      scoringFormat,
      teamCount: 12,
      isSuperflex: leagueFormat === 'superflex' || leagueFormat === '2qb',
    });
  }, [leagueFormat, scoringFormat]);

  const { players: playersPool, isLoading: playersLoading } = useSharedPlayerData({
    leagueConfig,
    scaleToTen: true,
    scoringFormat,
  });

  const labeledPool = useMemo(
    () => playersPool.map((player) => (
      player.position === 'DEF' ? { ...player, name: displayDstName(player.team) } : player
    )),
    [playersPool]
  );

  const rosterOnBoard = useMemo(
    () => rankForTrade(resolveOnBoard(activeRosterPlayers, labeledPool).map((player) => (
      player.position === 'DEF' ? { ...player, name: displayDstName(player.team) } : player
    ))),
    [activeRosterPlayers, labeledPool]
  );

  const usedIds = [...youSend, ...youGet].map((player) => player.id);
  const hasAny = youSend.length > 0 || youGet.length > 0;
  const hasBoth = youSend.length > 0 && youGet.length > 0;
  const verdict = hasAny ? tradeVerdict(youSend, youGet) : null;
  const tradeRead = verdict && hasBoth ? explainTrade(youSend, youGet, verdict) : null;
  const balance = verdict && hasBoth ? evenThis(verdict.you, verdict.them) : null;

  const balanceAdds = useMemo(() => {
    if (!balance) return [];
    const pool = balance.side === 'send' && hasRoster ? rosterOnBoard : labeledPool;
    return suggestReplacements(pool, balance.amount, usedIds, 3);
  }, [balance, hasRoster, rosterOnBoard, labeledPool, usedIds]);

  const nearbyNames = useMemo(() => {
    if (youSend.length > 0 && youGet.length === 0) {
      return suggestReplacements(labeledPool, playerTradeValue(youSend[0]) && youSend.length === 1
        ? playerTradeValue(youSend[0])
        : (verdict?.them ?? 0), usedIds, 3);
    }
    if (youGet.length > 0 && youSend.length === 0) {
      const pool = hasRoster ? rosterOnBoard : labeledPool;
      return suggestReplacements(pool, youGet.length === 1
        ? playerTradeValue(youGet[0])
        : (verdict?.you ?? 0), usedIds, 3);
    }
    return [];
  }, [youSend, youGet, labeledPool, rosterOnBoard, hasRoster, usedIds, verdict]);

  const packages = useMemo(() => {
    if (!hasBoth || !verdict) return [];
    const target = Math.max(verdict.you, verdict.them);
    const pool = verdict.you >= verdict.them && hasRoster ? rosterOnBoard : labeledPool;
    return suggestPackages(pool, target, usedIds, 2);
  }, [hasBoth, verdict, hasRoster, rosterOnBoard, labeledPool, usedIds]);

  const addPlayer = (player: Player, side: 'send' | 'get') => {
    const already = [...youSend, ...youGet].some((row) => String(row.id) === String(player.id));
    if (already) {
      toast.error(`${player.name} is already in this trade`);
      return;
    }
    if (side === 'send') setYouSend((prev) => [...prev, player]);
    else setYouGet((prev) => [...prev, player]);
  };

  const addPackage = (players: Player[], side: 'send' | 'get') => {
    players.forEach((player) => addPlayer(player, side));
  };

  const removePlayer = (playerId: string | number, side: 'send' | 'get') => {
    if (side === 'send') setYouSend((prev) => prev.filter((player) => String(player.id) !== String(playerId)));
    else setYouGet((prev) => prev.filter((player) => String(player.id) !== String(playerId)));
  };

  const clearTrade = () => {
    setYouSend([]);
    setYouGet([]);
  };

  const swapSides = () => {
    setYouSend(youGet);
    setYouGet(youSend);
  };

  if (playersLoading) {
    return <PageLoader pageName="Trade Calculator" loadingText="Loading..." />;
  }

  const verdictTone = !tradeRead || !verdict
    ? 'text-zinc-400'
    : verdict.winner === 'your-team'
      ? 'text-fiesta-yellow'
      : verdict.winner === 'their-team'
        ? 'text-fiesta-pink'
        : 'text-fiesta-green';

  const nearbySide: 'send' | 'get' = youSend.length > 0 && youGet.length === 0 ? 'get' : 'send';

  return (
    <PageShell>
      <Helmet>
        <title>Trade Calculator | Fantasy Fiesta</title>
        <meta name="description" content="See who wins a fantasy football trade in your league format." />
      </Helmet>

      <PageHeader
        title="Trade Calculator"
        subtitle="Same values as the rankings board. Add names and see who wins."
        actions={
          <div className="space-y-3">
            <div className="hidden sm:flex justify-center">
              <LeagueFormatSelector
                selectedFormat={leagueFormat}
                onFormatChange={setLeagueFormat}
                variant="pills"
              />
            </div>
            <div className="sm:hidden">
              <LeagueFormatSelector
                selectedFormat={leagueFormat}
                onFormatChange={setLeagueFormat}
                variant="dropdown"
              />
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {([
                { id: 'ppr', label: 'PPR' },
                { id: 'half-ppr', label: 'Half PPR' },
                { id: 'standard', label: 'Standard' },
              ] as const).map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setScoringFormat(option.id)}
                  className={`rounded-full border px-3 py-1.5 text-sm ${
                    scoringFormat === option.id
                      ? 'border-fiesta-blue bg-fiesta-blue/15 text-fiesta-blue'
                      : 'border-white/10 bg-white/5 text-zinc-300'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        }
      />

      <Surface className="p-5 text-center sm:p-6">
        {tradeRead && verdict ? (
          <>
            <p className={`font-display text-2xl font-semibold ${verdictTone}`}>
              {tradeRead.headline}
            </p>
            <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-zinc-400">
              {tradeRead.detail}
            </p>
            <div className="mx-auto mt-5 h-2 max-w-md overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full bg-fiesta-yellow"
                style={{
                  width: `${Math.min(100, (verdict.you / Math.max(verdict.you + verdict.them, 0.01)) * 100)}%`,
                }}
              />
            </div>
            <p className="mt-3 text-sm text-zinc-400">
              You get {verdict.you.toFixed(1)} · you send {verdict.them.toFixed(1)}
              {Math.abs(verdict.gap) >= 0.45 ? ` · gap ${Math.abs(verdict.gap).toFixed(1)}` : ''}
            </p>
          </>
        ) : hasAny ? (
          <p className="text-sm text-zinc-400">
            {youSend.length > 0
              ? 'Now add who you would get back.'
              : 'Now add who you would send.'}
          </p>
        ) : (
          <p className="text-sm text-zinc-400">
            {hasRoster
              ? 'Open your roster and tap who you would send, then search who you want back.'
              : 'Add a player on either side to grade the trade.'}
          </p>
        )}
        {hasAny ? (
          <div className="mt-4 flex flex-wrap items-center justify-center gap-4">
            {hasBoth ? (
              <button
                type="button"
                onClick={swapSides}
                className="text-sm text-zinc-400 hover:text-white"
              >
                Swap sides
              </button>
            ) : null}
            <button
              type="button"
              onClick={clearTrade}
              className="text-sm text-zinc-400 hover:text-white"
            >
              Clear trade
            </button>
          </div>
        ) : null}
      </Surface>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Surface className="p-4 sm:p-5">
          <div className="mb-3 flex items-end justify-between gap-3">
            <div>
              <h2 className="font-medium text-white">You send</h2>
              {hasRoster ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setSendSource('roster')}
                    className={`rounded-full border px-3 py-1 text-xs ${
                      rosterMode
                        ? 'border-fiesta-yellow bg-fiesta-yellow text-ink'
                        : 'border-white/10 bg-white/5 text-zinc-400 hover:border-white/20'
                    }`}
                  >
                    Your roster
                  </button>
                  <button
                    type="button"
                    onClick={() => setSendSource('all')}
                    className={`rounded-full border px-3 py-1 text-xs ${
                      !rosterMode
                        ? 'border-fiesta-yellow bg-fiesta-yellow text-ink'
                        : 'border-white/10 bg-white/5 text-zinc-400 hover:border-white/20'
                    }`}
                  >
                    All players
                  </button>
                </div>
              ) : (
                <p className="mt-1 text-xs text-zinc-500">
                  <Link to="/my-roster" className="text-fiesta-yellow hover:underline">
                    Import a roster
                  </Link>
                  {' '}to pick from your team.
                </p>
              )}
            </div>
            <p className="text-lg font-semibold text-fiesta-yellow">{(verdict?.them ?? 0).toFixed(1)}</p>
          </div>

          {rosterMode ? (
            <RosterPicker
              players={rosterOnBoard}
              excludeIds={usedIds}
              onSelect={(player) => addPlayer(player, 'send')}
            />
          ) : (
            <TradeSearch
              onSearch={(query) => searchPlayers(labeledPool, query)}
              onSelect={(player) => addPlayer(player, 'send')}
              placeholder="Search players to send"
            />
          )}

          <SidePlayers
            players={youSend}
            emptyLabel={rosterMode ? 'Tap someone above to add them.' : 'No one on this side yet.'}
            onRemove={(id) => removePlayer(id, 'send')}
          />
        </Surface>

        <Surface className="p-4 sm:p-5">
          <div className="mb-3 flex items-end justify-between gap-3">
            <div>
              <h2 className="font-medium text-white">You get</h2>
              <p className="mt-1 text-xs text-zinc-500">Search the board for who you want back.</p>
            </div>
            <p className="text-lg font-semibold text-fiesta-yellow">{(verdict?.you ?? 0).toFixed(1)}</p>
          </div>
          <TradeSearch
            onSearch={(query) => searchPlayers(labeledPool, query)}
            onSelect={(player) => addPlayer(player, 'get')}
            placeholder="Search players to receive"
          />
          <SidePlayers
            players={youGet}
            emptyLabel="No one on this side yet."
            onRemove={(id) => removePlayer(id, 'get')}
          />
        </Surface>
      </div>

      {nearbyNames.length > 0 ? (
        <Surface className="mt-4 p-5">
          <h2 className="mb-1 font-medium text-white">
            {nearbySide === 'get' ? 'Names near what you are sending' : 'Names near what you would get'}
          </h2>
          <p className="mb-3 text-sm text-zinc-500">
            {nearbySide === 'get'
              ? 'Start the other side with someone in this range.'
              : hasRoster
                ? 'Pieces on your roster in this range.'
                : 'Board names in this range.'}
          </p>
          <SuggestionGrid
            players={nearbyNames}
            onSelect={(player) => addPlayer(player, nearbySide)}
          />
        </Surface>
      ) : null}

      {balance && hasBoth ? (
        <Surface className="mt-4 p-5">
          <h2 className="mb-1 font-medium text-white">
            {balance.side === 'send' ? 'Add to you send to even this' : 'Add to you get to even this'}
          </h2>
          <p className="mb-3 text-sm text-zinc-500">
            Looking for about {balance.amount.toFixed(1)} more on that side.
          </p>
          {balanceAdds.length === 0 ? (
            <p className="text-sm text-zinc-500">
              {hasRoster && balance.side === 'send'
                ? 'No nearby leftover piece on your roster.'
                : 'No single name sits that close on this board.'}
            </p>
          ) : (
            <SuggestionGrid
              players={balanceAdds}
              onSelect={(player) => addPlayer(player, balance.side)}
            />
          )}
        </Surface>
      ) : null}

      {packages.length > 0 && hasBoth ? (
        <Surface className="mt-4 p-5">
          <h2 className="mb-3 font-medium text-white">Two-for-one packages</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {packages.map((pair) => (
              <button
                key={pair.map((player) => String(player.id)).join('-')}
                type="button"
                onClick={() => addPackage(pair, verdict && verdict.you >= verdict.them ? 'send' : 'get')}
                className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-left hover:border-fiesta-yellow/40"
              >
                <p className="text-sm text-white">
                  {pair.map((player) => player.name).join(' and ')}
                </p>
                <p className="mt-1 text-xs text-zinc-500">
                  {(playerTradeValue(pair[0]) + playerTradeValue(pair[1])).toFixed(1)} combined
                </p>
              </button>
            ))}
          </div>
        </Surface>
      ) : null}
    </PageShell>
  );
};

function SidePlayers({
  players,
  emptyLabel,
  onRemove,
}: {
  players: Player[];
  emptyLabel: string;
  onRemove: (id: string | number) => void;
}) {
  if (players.length === 0) {
    return <p className="pt-6 text-center text-sm text-zinc-600">{emptyLabel}</p>;
  }

  return (
    <div className="mt-4 space-y-2">
      {players.map((player) => (
        <div key={String(player.id)} className="flex items-center gap-2 rounded-xl bg-black/20 px-2 py-2">
          <PlayerPhoto
            name={player.name}
            position={player.position}
            team={player.team}
            sleeperId={player.id}
            espnId={player.espn_id}
            imageUrl={player.imageUrl}
            className="h-8 w-8 rounded-full object-cover"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm text-white">{player.name}</p>
            <p className="text-xs text-zinc-500">
              {player.rank ? `#${player.rank} · ` : ''}
              {player.position === 'DEF' ? 'D/ST' : player.position} · {player.team}
            </p>
          </div>
          <span className="text-xs text-zinc-400">{playerTradeValue(player).toFixed(1)}</span>
          <button
            type="button"
            onClick={() => onRemove(player.id)}
            className="px-2 text-zinc-500 hover:text-white"
            aria-label={`Remove ${player.name}`}
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );
}

function SuggestionGrid({
  players,
  onSelect,
}: {
  players: Player[];
  onSelect: (player: Player) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {players.map((player) => (
        <button
          key={String(player.id)}
          type="button"
          onClick={() => onSelect(player)}
          className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-left hover:border-fiesta-yellow/40"
        >
          <div className="flex items-center gap-2">
            <PlayerPhoto
              name={player.name}
              position={player.position}
              team={player.team}
              sleeperId={player.id}
              espnId={player.espn_id}
              imageUrl={player.imageUrl}
              className="h-8 w-8 rounded-full object-cover"
            />
            <div className="min-w-0">
              <p className="truncate font-medium text-white">{player.name}</p>
              <p className="text-xs text-zinc-500">
                {player.position === 'DEF' ? 'D/ST' : player.position} · {player.team} · {playerTradeValue(player).toFixed(1)}
              </p>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

export default TradeCalculator;
